import { useParams, Link, useNavigate } from "react-router-dom"
import { useState, useContext, useEffect } from "react"
import { RatingStars } from "../global/RatingStars"
import { CustomButton } from "../global/CustomButton"
import { UserContext } from "../../contexts/UserContext"
import { userReducer } from "../../contexts/UserContext"
import { handleUpdateArray } from "../../functions/UpdateUser"
import { ProductsContext } from "../../contexts/ProductsContext"
import "./itemSection.css"

export function ItemSection() {
    const { id } = useParams()

    const productsDataContext = useContext(ProductsContext)
    const { products } = productsDataContext

    const userDataContext = useContext(UserContext)
    const { user } = userDataContext

    const [product, setProduct] = useState(null)
    const [quantity, setQuantity] = useState(1)
    const [activeColorIndex, setActiveColorIndex] = useState(0)
    const [activeSizeIndex, setActiveSizeIndex] = useState(0)
    const [inWishlist, setInWishlist] = useState(null)
    
    const navigate = useNavigate()

    useEffect(() => {
        if(products) {
            const findProduct = products.find(e => e._id === id)
            setProduct(findProduct)
        }
    }, [products])

    useEffect(() => {
        if(user) {
            setInWishlist(user.wishlist.find(e => e._id === id))
        }
    }, [user])

    function updateWishlist() {
        let userDataClone = { userData: { user } }

        if(inWishlist) {
            userDataClone = userReducer(userDataClone, { type: "REMOVE_FROM_WISHLIST", payload: id })
            const updatedWishlist = userDataClone.userData.user.wishlist
            handleUpdateArray(updatedWishlist, "REMOVE_FROM_WISHLIST", id, userDataContext)
        } else {
            userDataClone = userReducer(userDataClone, { type: "ADD_TO_WISHLIST", payload: product })
            const updatedWishlist = userDataClone.userData.user.wishlist
            handleUpdateArray(updatedWishlist, "ADD_TO_WISHLIST", product, userDataContext)
        } 
    }

    function createCartItem() {
        const productActiveColor = product.colours ? product.colours[activeColorIndex] : null
        const productActiveSize = product.sizes ? product.sizes[activeSizeIndex] : null
        
        const newCartItem = {
            ...product,
            customId: crypto.randomUUID(),
            quantity: quantity,
            color: product.colours ? productActiveColor : null,
            size: product.sizes ? productActiveSize : null,
            type: product.colours || product.sizes ? "variant" : "normal"
        }

        return newCartItem
    }

    function cartUpdatePayload(checkItem) {
        const filteredCart = user.cart.filter(e => e.customId !== checkItem.customId)
            
        const updatePayload = {
            filteredCart: filteredCart,
            updatedQuantity: checkItem.quantity + quantity,
            existingItem: checkItem
        }

        return updatePayload
    }

    function updateCart() {
        const productActiveColor = product.colours ? product.colours[activeColorIndex] : null
        const productActiveSize = product.sizes ? product.sizes[activeSizeIndex] : null

        const itemExists = user.cart.find(e => e._id === id)

        const variantExists = user.cart.find(e =>
            product.colours &&
            product.sizes &&
            e._id === id &&
            e.color === productActiveColor &&
            e.size === productActiveSize
        )

        const newCartItem = createCartItem()

        let userDataClone = { userData: { user } }

        if(variantExists) {
            const updatePayload = cartUpdatePayload(variantExists)
            userDataClone = userReducer(userDataClone, { type: "UPDATE_CART_ITEM", payload: updatePayload })
            const updatedCart = userDataClone.userData.user.cart
            handleUpdateArray(updatedCart, "UPDATE_CART_ITEM", updatePayload, userDataContext)
        } else if(itemExists && newCartItem.type === "normal") {
            const updatePayload = cartUpdatePayload(itemExists)
            userDataClone = userReducer(userDataClone, { type: "UPDATE_CART_ITEM", payload: updatePayload })
            const updatedCart = userDataClone.userData.user.cart
            handleUpdateArray(updatedCart, "UPDATE_CART_ITEM", updatePayload, userDataContext)
        } else {
            userDataClone = userReducer(userDataClone, { type: "ADD_TO_CART", payload: newCartItem })
            const updatedCart = userDataClone.userData.user.cart
            handleUpdateArray(updatedCart, "ADD_TO_CART", newCartItem, userDataContext)
        }
    }

    return product && (
        <div className="d-flex justify-content-between single-details mb-5 lg-row">
            {/* Product image */}
            <div className="px-4 py-5 border rounded-1 d-flex me-auto">
                <div className="single-image my-auto" style={{
                    backgroundImage: `url(${product.image})`
                }}>
                </div>
            </div>

            <div className="d-flex flex-column single-information ms-auto">

                <h4>{product.title}</h4>
                {/* Stars, reviews and stock */}
                <div className="d-flex align-items-center single-p-rating mt-1 mb-2">
                    <RatingStars rating={product.rating.rate} />
                    <div className="single-reviews">
                        {`(${product.rating.reviews} Reviews)`}
                    </div>
                    
                    {
                        product.inStock && (
                            <div className="in-stock d-flex">
                                <div className="mx-3">|</div>
                                <div>In Stock</div>
                            </div>
                        )
                    }
                </div>

                <h4 className="fw-normal">
                    {`$${product.salePrice ? product.salePrice : product.regularPrice}`}
                </h4>
                    
                <div className="single-description pb-4 pt-2 mb-2">{product.description}</div>
                {/* Product colors */}
                {
                    product.colours && (
                        <div className="d-flex gap-3 align-items-center my-2">
                            <h5 className="fw-normal">Colours: </h5>
                            <div className="d-flex gap-2 pb-1">
                                {
                                    product.colours.map((e, i) => (
                                        <div
                                            key={i}
                                            onClick={() => setActiveColorIndex(i)}
                                            className={`d-flex cntr product-color rounded-circle ${activeColorIndex === i ? "product-color-active" : ""}`}>
                                            <div className="rounded-circle" style={{
                                                backgroundColor: e
                                            }}></div>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    )
                }
                {/* Product sizes */}
                {
                    product.sizes && (
                        <div className="d-flex gap-4 my-1">
                            <h5 className="fw-normal">Size: </h5>
                            <div className="d-flex align-items-center single-sizes gap-2">
                                {
                                    product && product.sizes.map((e, i) => (
                                        <div
                                            key={i}
                                            onClick={() => setActiveSizeIndex(i)}
                                            className={`d-flex cntr ${activeSizeIndex === i ? "size-active" : ""}`}>
                                            {e}
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    )
                }
                
                {/* Quantity section and wishlist */}
                <div className="d-flex align-items-stretch my-3">

                    <div className="d-flex align-items-stretch quantity-section me-auto">
                        <div
                            className="d-flex cntr quantity-btns"
                            onClick={() => quantity > 1 && setQuantity(quantity - 1)}>
                        </div>
                        <input
                            type="number"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                        />
                        <div
                            className="d-flex cntr quantity-btns"
                            onClick={() => setQuantity(quantity + 1)}>
                        </div>
                    </div>

                    <div
                        onClick={user ? () => updateWishlist() : () => navigate("/login")}
                        className={`single-heart rounded-1 ms-2 ${inWishlist ? "wishlist-heart" : "heart-icon"}`}>
                    </div>

                </div>

                {/* Buttons section */}
                <div className="d-flex gap-2 mb-3 product-buttons-section">
                    <div>
                        <CustomButton
                            color="default"
                            text="Add To Cart"
                            btnFunction={user ? () => updateCart() : () => navigate("/login")}
                        />
                    </div>
                    <div>
                        <CustomButton
                            color="primary"
                            text="Buy Now"
                            btnFunction={user ? 
                                () => navigate("/cart/checkout", {
                                    state: {
                                        fromItem: true,
                                        item: { ...product, customId: crypto.randomUUID() },
                                        subtotal: (
                                            product.salePrice ? product.salePrice : product.regularPrice
                                        )
                                    }
                                })
                                :
                                () => navigate("/login")
                            }
                        />
                    </div>
                </div>

                {/* Delivery info section */}
                <div className="d-flex flex-column rounded-1 product-info mt-auto">
                    <div className="d-flex gap-3 align-items-center free-delivery px-3 py-4">
                        <div className="delivery-icon"></div>
                        <div>
                            <div className="info">Free Delivery</div>
                            <div className="more-info">
                                <Link>Enter your postal code for Delivery Availability</Link>
                            </div>
                        </div>
                    </div>
                    <div className="d-flex gap-3 align-items-center free-return px-3 py-4">
                        <div className="return-icon"></div>
                        <div>
                            <div className="info">Return Delivery</div>
                            <div className="more-info">
                                Free 30 Days Delivery Returns. <Link>Details</Link>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

        </div>
    )
}